// SPDX-License-Identifier: Elastic-2.0
/**
 * Icon — inline-SVG, stroke-only icon set.
 *
 * Renders an entry from `iconPaths.ts` inside a 24×24 `viewBox`. Stroke
 * properties (`stroke`, `strokeWidth`, `strokeLinecap`, `strokeLinejoin`)
 * are set on the parent `<svg>` so individual path entries stay
 * minimal — they only need geometry. The visual contract matches the
 * Lucide style copied from `DashboardShell.Icon`.
 *
 * Lookup failures: in development we `console.warn` once per missing
 * name to surface typos; in production the component returns `null`
 * so a stale name doesn't blow up the page. Both paths are exercised
 * by the test suite.
 *
 * Decorative by default (`aria-hidden`). Callers that want an
 * announceable icon pass a `title` — we render an `<svg><title>` and
 * set `role="img"` so screen readers pick it up.
 */
import type { CSSProperties } from "react";

import { ICON_PATHS, type IconName } from "./iconPaths";

export type { IconName } from "./iconPaths";

export interface IconProps {
  /** Icon name. Must be a key of `ICON_PATHS`. */
  name: IconName;
  /** Square render size in pixels. Defaults to 18 — DashboardShell norm. */
  size?: number;
  /** Stroke colour. Defaults to `currentColor` so the icon inherits text. */
  color?: string;
  /** Stroke width. Defaults to 1.75 to match Lucide / DashboardShell. */
  stroke?: number;
  /**
   * Optional accessible label. When set the SVG becomes `role="img"` with
   * an `<title>` child; when omitted (default) the SVG is `aria-hidden`.
   */
  title?: string;
  /** Optional class for layout/positioning overrides. */
  className?: string;
  /** Optional inline style overrides (rare — prefer className). */
  style?: CSSProperties;
}

// Track names we've already warned about to avoid console spam if the
// same bad name appears in a loop.
const warnedNames = new Set<string>();

function warnMissing(name: string): void {
  if (warnedNames.has(name)) {
    return;
  }
  warnedNames.add(name);
  // `process` may not exist in pure browser builds — guard the lookup.
  const isProd =
    typeof process !== "undefined" && process.env?.NODE_ENV === "production";
  if (!isProd) {
    // eslint-disable-next-line no-console
    console.warn(`[Icon] Unknown icon name: "${name}"`);
  }
}

export function Icon({
  name,
  size = 18,
  color = "currentColor",
  stroke = 1.75,
  title,
  className,
  style,
}: IconProps): JSX.Element | null {
  const paths = ICON_PATHS[name];
  if (!paths) {
    warnMissing(name);
    return null;
  }
  const decorative = title === undefined;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={decorative ? undefined : "img"}
      aria-hidden={decorative || undefined}
      aria-label={decorative ? undefined : title}
      className={className}
      data-testid={`icon-${name}`}
      data-icon={name}
      style={{ flexShrink: 0, ...style }}
    >
      {title ? <title>{title}</title> : null}
      {paths}
    </svg>
  );
}
